import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-client';
import type {
  CreateGearItemPayload,
  GearItem,
  GearItemUsage,
  PaginatedResponse,
} from '@/lib/types';

export function useGearItems() {
  return useQuery({
    queryKey: queryKeys.gearItems.list({}),
    queryFn: async (): Promise<PaginatedResponse<GearItem>> => {
      const response = await apiClient.get<PaginatedResponse<GearItem>>(
        '/api/v1/gear-items/',
        { params: { page_size: 200 } },
      );
      return response.data;
    },
  });
}

export function useGearUsage() {
  return useQuery({
    queryKey: queryKeys.gearItems.usage(),
    queryFn: async (): Promise<GearItemUsage> => {
      const response = await apiClient.get<GearItemUsage>('/api/v1/gear-items/usage/');
      return response.data;
    },
  });
}

export function useCreateGearItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateGearItemPayload): Promise<GearItem> => {
      const response = await apiClient.post<GearItem>('/api/v1/gear-items/', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.gearItems.all });
    },
  });
}

export function useUpdateGearItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number;
      data: Partial<CreateGearItemPayload>;
    }): Promise<GearItem> => {
      const response = await apiClient.patch<GearItem>(`/api/v1/gear-items/${id}/`, data);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.gearItems.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.gearItems.detail(variables.id) });
    },
  });
}

export function useDeleteGearItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await apiClient.delete(`/api/v1/gear-items/${id}/`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.gearItems.all });
    },
  });
}
